<template>
  <div>
    <Section>
      <SectionHeader :title="$t('apps.hrm.message.general')"/>
      <FormRow>
        <Col className="col-lg-6">
        <Label :text="`${$t('apps.hrm.message.fullname')} *`" />
        <BaseInput
          v-model="$v.input.name.$model"
          :classNames="[{ 'is-invalid': $v.input.name.$error }]"
          :disabled="disabled"
        />
        </Col>
        <Col className="col-lg-2">
        <Label :text="$t('apps.hrm.message.gender')" />
        <BaseDropdown
          v-model="input.gender"
          :options="genderOptions"
          :disabled="disabled"
        />
        </Col>
        <Col className="col-lg-2">
        <Label :text="$t('apps.hrm.message.birthdate')" />
        <BaseInputDatePicker
          v-model="input.birthdate"
          :disabled="disabled"
        />
        </Col>
      </FormRow>
      <FormRow>
        <Col className="col-lg-3">
        <Label :text="$t('apps.hrm.message.department')" />
        <BaseDropdown
          v-model="input.departmentId"
          :options="departmentOptions"
          :disabled="disabled"
        />
        </Col>
        <Col className="col-lg-3">
        <Label :text="$t('apps.hrm.message.position')" />
        <BaseDropdown
          v-model="input.positionId"
          :options="positionOptions"
          :disabled="disabled"
        />
        </Col>
      </FormRow>
    </Section>

    <Section>
      <SectionHeader :title="$t('apps.hrm.message.contacts')"/>
      <FormRow>
        <Col className="col-lg-3">
        <Label :text="$t('apps.hrm.message.tel')" />
        <BaseInput
          v-model="input.tel"
          :disabled="disabled"
        />
        </Col>
        <Col className="col-lg-3">
        <Label :text="$t('apps.hrm.message.email')" />
        <BaseInput
          v-model="input.email"
          :disabled="disabled"
        />
        </Col>
      </FormRow>
      <FormRow>
        <Col className="col-lg-6">
        <Label :text="$t('apps.hrm.message.address')" />
        <BaseTextArea
          v-model="input.address"
          :disabled="disabled"
          :rows="3"
        />
        </Col>
      </FormRow>
    </Section>

    <Section>
      <SectionHeader :title="$t('apps.hrm.message.userinfoincompany')"/>
      <FormRow>
        <Col className="col-lg-2">
        <Label :text="$t('apps.hrm.message.wageamount')" />
        <BaseInputNumber
          v-model="input.wageAmount"
          :disabled="disabled"
        />
        </Col>
        <Col className="col-lg-2">
        <Label :text="$t('apps.hrm.message.wagetype')" />
        <BaseDropdown
          v-model="input.wageType"
          :options="wageOptions"
          :disabled="disabled"
        />
        </Col>
      </FormRow>
      <FormRow>
        <Col className="col-lg-2">
        <Label :text="$t('apps.hrm.message.startworkingdate')" />
        <BaseInputDatePicker
          v-model="input.startWorkingDate"
          :disabled="disabled"
        />
        </Col>
      </FormRow>
      <FormRow>
        <Col className="col-lg-6">
        <Label :text="$t('apps.hrm.message.note')" />
        <BaseTextArea
          v-model="input.note"
          :disabled="disabled"
          :rows="3"
        />
        </Col>
      </FormRow>
      <FormRow>
        <Col className="col-lg-6">
        <Label :text="$t('apps.hrm.message.tag')" />
        <BaseInputTag
          v-model="input.tags"
          :disabled="disabled"
        />
        </Col>
      </FormRow>
    </Section>

    <BaseFormAction
      v-if="showActionButtons"
      :textCancel="$t('button.cancel')"
      :textSubmit="$t('button.save')"
      :isSubmitted="isSubmitted"
      @onCancel="handleCancel"
      @onSubmit="handleSubmit"
    />
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import _ from 'lodash'
import { toDate } from '@/helpers'
import { GENDER, WAGE_TYPE } from '@/helpers/enum'

export default {
  props: ['state', 'isSubmitted', 'disabled', 'departments', 'positions', 'showActionButtons'],

  data () {
    return {
      input: {
        name: null,
        gender: null,
        birthdate: null,
        departmentId: null,
        positionId: null,
        tel: null,
        email: null,
        address: null,
        wageAmount: null,
        wageType: null,
        startWorkingDate: null,
        note: null,
        tags: [],
      },
      inputOriginal: null,
    }
  },

  validations: {
    input: {
      name: {
        required,
      },
    },
  },

  computed: {
    defaultOption () {
      return {
        text: this.$t('placeholder.select'),
        value: null,
      }
    },
    genderOptions () {
      return [
        { ...this.defaultOption },
        { text: this.$t('apps.hrm.message.male'), value: GENDER.MALE },
        { text: this.$t('apps.hrm.message.female'), value: GENDER.FEMALE },
      ]
    },
    departmentOptions () {
      const items = this.departments.map((item) => ({ text: item.name, value: item.depId }))
      return [ { ...this.defaultOption }, ...items]
    },
    positionOptions () {
      const items = this.positions.map((item) => ({ text: item.name, value: item.positionId }))
      return [ { ...this.defaultOption }, ...items]
    },
    wageOptions () {
      return [
        { ...this.defaultOption },
        { text: this.$t('apps.hrm.message.wagetypedaily'), value: WAGE_TYPE.DAILY },
        { text: this.$t('apps.hrm.message.wagetypemonthly'), value: WAGE_TYPE.MONTHLY },
      ]
    },
  },

  watch: {
    input: {
      handler: function () {
        this.$emit('update:form', this.input)
      },
      deep: true,
    },
  },

  created () {
    if (this.state) {
      this.setInput(this.state)
    } else {
      this.inputOriginal = _.cloneDeep(this.input)
    }
  },

  methods: {
    handleCancel () {
      if (this.$listeners.onCancel) {
        this.$emit('onCancel')
      } else {
        this.input = _.cloneDeep(this.inputOriginal)
        this.scrollToTop(600)
      }
    },
    handleSubmit () {
      this.$v.$touch()
      if (this.$v.$invalid) {
        this.scrollToTop(600)
      } else {
        this.$emit('onSubmit', this.input)
      }
    },
    setInput (state) {
      this.input.name = state.name
      this.input.gender = state.gender
      this.input.birthdate = toDate(state.birthdate)
      this.input.departmentId = state.departmentId
      this.input.positionId = state.positionId
      this.input.tel = state.tel
      this.input.email = state.email
      this.input.address = state.address
      this.input.wageAmount = state.wageAmount
      this.input.wageType = state.wageType
      this.input.startWorkingDate = toDate(state.startWorkingDate)
      this.input.note = state.note
      this.input.tags = state.tags
      this.inputOriginal = _.cloneDeep(this.input)
    },
  },
}
</script>



// WEBPACK FOOTER //
// src/components/combine/HrmEmployeeForm.vue